package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.Adjustment
import com.evoapps.evoshift.domain.model.AdjustmentType
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.InvalidAdjustmentException
import com.evoapps.evoshift.domain.model.InvalidAdjustmentException.Problem
import com.evoapps.evoshift.domain.model.MonthLockedException
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlin.time.Instant

/*
 * SH-78: bonuses, deductions and expense refunds ("adjustments") of one person in one month.
 * They are part of the labour cost estimate (GeneratePayrollReportUseCase) and of the person's
 * own pay estimate, never a payroll: the accountant settles the actual pay.
 *
 * Who: owners and financial managers (Gate.ADJUSTMENTS_EDITING; firestore.rules isFinancialManager)
 * while the plan runs (SH-65). When: until the month is LOCKED; a locked month's adjustments are
 * history the accountant may already have used, so they can be seen but not added, changed or
 * removed (checked here and in firestore.rules).
 */

/** An adjustment as typed: what kind, how much (positive, in the workplace's currency), which day, why. */
data class AdjustmentInput(
    val type: AdjustmentType,
    val amount: Double,
    val date: String,
    val description: String,
    val relatedShiftId: String? = null
)

/** Fails with [MonthLockedException] when [rosterId] is LOCKED. A roster that cannot be read is left to the rules. */
internal suspend fun ensureMonthOpen(rosterRepository: RosterRepository, rosterId: String) {
    if (rosterRepository.getRoster(rosterId).getOrNull()?.status == RosterStatus.LOCKED) throw MonthLockedException(rosterId)
}

/**
 * Adds an adjustment for [userId] in [rosterId]'s month, or changes one ([editingId]). Checks a
 * description, a positive amount, a day inside the month and that the month is not locked.
 * Audit: a new one is stamped createdBy/createdAt with [editorId]; a change keeps those and stamps
 * updatedBy/updatedAt.
 */
class SaveAdjustmentUseCase(
    private val financialRepository: FinancialRepository,
    private val rosterRepository: RosterRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(
        rosterId: String,
        userId: String,
        input: AdjustmentInput,
        editorId: String,
        editingId: String? = null
    ): Result<Adjustment> = runCatching {
        val description = input.description.trim()
        if (description.isEmpty()) throw InvalidAdjustmentException(Problem.DESCRIPTION_MISSING)
        if (!(input.amount > 0.0) || input.amount.isInfinite()) throw InvalidAdjustmentException(Problem.AMOUNT_NOT_POSITIVE)
        val date = runCatching { LocalDate.parse(input.date) }.getOrNull()
        val (year, month) = RosterIds.yearMonthOf(rosterId)
        if (date == null || date.year != year || date.month.number != month) throw InvalidAdjustmentException(Problem.DATE_OUTSIDE_MONTH)
        ensureMonthOpen(rosterRepository, rosterId)

        val previous = editingId?.let { id ->
            financialRepository.getAdjustment(id).getOrThrow()
                .takeIf { it.rosterId == rosterId && it.userId == userId }
                ?: throw NotFoundException("Adjustment $id of $userId in $rosterId")
        }
        val currency = workplaceRepository.getWorkplace(RosterIds.workplaceIdOf(rosterId)).getOrNull()?.settings?.currency
            ?: previous?.currency ?: DEFAULT_CURRENCY
        val stamp = now().toString()
        financialRepository.saveAdjustment(
            Adjustment(
                id = editingId.orEmpty(),
                userId = userId,
                rosterId = rosterId,
                type = input.type,
                amount = input.amount,
                currency = currency,
                description = description,
                date = date.toString(),
                relatedShiftId = input.relatedShiftId?.takeIf { it.isNotBlank() },
                metadata = previous?.metadata?.copy(updatedAt = stamp, updatedBy = editorId)
                    ?: AuditMetadata(createdAt = stamp, createdBy = editorId)
            )
        ).getOrThrow()
    }

    private companion object {
        const val DEFAULT_CURRENCY = "PLN"
    }
}

/** Removes an adjustment of [rosterId]'s month, unless the month is locked. One already gone is fine. */
class DeleteAdjustmentUseCase(
    private val financialRepository: FinancialRepository,
    private val rosterRepository: RosterRepository
) {
    suspend operator fun invoke(rosterId: String, adjustmentId: String): Result<Unit> = runCatching {
        ensureMonthOpen(rosterRepository, rosterId)
        financialRepository.deleteAdjustment(adjustmentId).getOrThrow()
    }
}
